import { logError } from '@/lib/sentry.lib';

import { translate } from '@repo/phrase';
import type { AssetArticle, AssetType } from '@repo/types/asset';
import { concatNonEmptyStrings, copyTablesToClipboard, getNotifier } from '@repo/utils';
import { useMutation } from '@tanstack/react-query';

import { getArticleNo } from '@/utils/article.utils';

import { tableHeader } from './asset-summary-modal-article-table';

interface Props {
    onClose: () => void;
    items: AssetArticle[];
    assetId: string;
    assetType: AssetType;
    orderType?: string | undefined;
    createdAt: string;
}

export const useCopyAssetSummaryToClipboard = ({ items, assetType, createdAt, assetId, orderType, onClose }: Props) => {
    const notifier = getNotifier();

    const copyToClipboardMutation = useMutation({
        mutationFn: async () => {
            const summaryTable = {
                cells: [
                    [translate(`asset_summary_modal_details_id_${orderType ?? assetType}`), assetId],
                    [translate('asset_summary_modal_details_creation_date'), createdAt],
                ],
            };

            const articleTable = {
                cells: items.map((item) => [
                    item.itemId ? getArticleNo(item.itemId) : '',
                    concatNonEmptyStrings([item.name, item.description]),
                    item.quantity ?? '',
                    item.unitPrice?.toFixed(2) ?? '',
                    item.totalPrice?.toFixed(2) ?? '',
                ]),
                headers: tableHeader.map(({ text }) => translate(text)),
            };

            return copyTablesToClipboard([summaryTable, articleTable]);
        },
        onError: (error) => {
            notifier?.add({
                title: translate('asset_summary_modal_copy_to_clipboard_error_title'),
                message: translate('asset_summary_modal_copy_to_clipboard_error_body', {
                    number: assetId,
                }),
                type: 'error',
            });
            onClose();
            logError(error);
        },
    });

    return copyToClipboardMutation;
};
